import { useEffect, useState } from 'react'
import { Modal } from './Modal'
import { Download, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { syncAllDataPaginated, type FullSyncStatus } from '../../services/fullSyncService'
import { Button } from './Button'

interface SyncModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete?: () => void
}

export function SyncModal({ isOpen, onClose, onComplete }: SyncModalProps) {
  const [syncStatus, setSyncStatus] = useState<FullSyncStatus | null>(null)
  const [syncing, setSyncing] = useState(false)

  const handleSync = async () => {
    setSyncing(true)
    
    await syncAllDataPaginated((status) => {
      setSyncStatus(status)
    })

    setSyncing(false)
    
    // Chama callback de conclusão após um pequeno delay
    setTimeout(() => {
      if (onComplete) onComplete()
    }, 1000)
  }

  // Auto-inicia a sincronização quando o modal abre
  useEffect(() => {
    if (isOpen && !syncing && !syncStatus) {
      handleSync()
    }
  }, [isOpen])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'syncing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
    }
  }

  const formatEntityName = (entity: string) => {
    const names: Record<string, string> = {
      clientes: 'Clientes',
      industrias: 'Indústrias',
      produtos: 'Produtos',
      pedidos: 'Pedidos',
    }
    return names[entity] || entity
  }

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    }
    return `${seconds}s`
  }

  const allCompleted = syncStatus?.progress.every(p => p.status === 'completed') ?? false
  const hasErrors = syncStatus?.progress.some(p => p.status === 'error') ?? false

  return (
    <Modal
      isOpen={isOpen}
      onClose={allCompleted || hasErrors ? onClose : () => {}}
      title="Sincronização de Dados"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header com ícone */}
        <div className="flex items-center justify-center">
          <div className="rounded-full bg-blue-100 p-4 dark:bg-blue-900/30">
            <Download className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        {/* Descrição */}
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {syncing
              ? 'Sincronizando todos os dados da API para o banco local...'
              : allCompleted
              ? 'Sincronização concluída com sucesso!'
              : hasErrors
              ? 'Sincronização concluída com alguns erros'
              : 'Preparando sincronização...'}
          </p>
        </div>

        {/* Progress das entidades */}
        <div className="space-y-4">
          {syncStatus?.progress.map((progress) => (
            <div key={progress.entity} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(progress.status)}
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatEntityName(progress.entity)}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {progress.current > 0 ? `${progress.current} / ${progress.total}` : '—'}
                </span>
              </div>

              {/* Barra de progresso */}
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-full transition-all duration-300 ${
                    progress.status === 'error'
                      ? 'bg-red-500'
                      : progress.status === 'completed'
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>

              {/* Mensagem de erro */}
              {progress.error && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {progress.error}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Resumo */}
        {syncStatus && (
          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400">Total de itens</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {syncStatus.totalItems.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Sincronizados</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {syncStatus.syncedItems.toLocaleString()}
                </p>
              </div>
              {syncStatus.endTime && syncStatus.startTime && (
                <>
                  <div className="col-span-2">
                    <p className="text-gray-600 dark:text-gray-400">Tempo decorrido</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatTime(syncStatus.endTime - syncStatus.startTime)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end gap-3">
          {(allCompleted || hasErrors) && (
            <Button onClick={onClose} variant="primary">
              Fechar
            </Button>
          )}
          {!syncing && !allCompleted && !hasErrors && (
            <Button onClick={handleSync} variant="primary">
              Iniciar Sincronização
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
